import React, { useState, useEffect } from 'react';
import './TicketVerification.css';
import logo from '../../images/logo.png';
import elfUpImage from '../../images/elf_up.png';
import identificationIcon from '../../images/identification.png';
import baseURL from '../url';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars, faSearch, faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { useNavigate } from 'react-router-dom';
import SideMenu from '../SideMenu/SideMenu';
import Footer from '../Footer/Footer';

export default function TicketVerification() {
    const [cedula, setCedula] = useState('');
    const [userTickets, setUserTickets] = useState([]);
    const [loading, setLoading] = useState(false);
    const [rifas, setRifas] = useState([]);
    const [ganadores, setGanadores] = useState([]);
    const [hasSearched, setHasSearched] = useState(false);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        cargarRifas();
        cargarGanadores();
    }, []);

    const cargarRifas = () => {
        fetch(`${baseURL}/rifaGet.php`, {
            method: 'GET',
        })
            .then(response => response.json())
            .then(data => {
                setRifas(data.rifas || []);
            })
            .catch(error => {

            });
    };

    const cargarGanadores = () => {
        fetch(`${baseURL}/ganadorGet.php`, {
            method: 'GET',
        })
            .then(response => response.json())
            .then(data => {
                setGanadores(data.ganadores || []);
            })
            .catch(error => {

            });
    };

    const handleBuscar = () => {
        if (!cedula.trim()) {
            alert('Por favor ingrese su número de cédula');
            return;
        }

        setHasSearched(true);
        setLoading(true);
        fetch(`${baseURL}/numeroGetByCedula.php?cedula=${encodeURIComponent(cedula.trim())}`, {
            method: 'GET',
        })
            .then(response => {
                // Verificar si la respuesta es exitosa
                if (!response.ok) {
                    throw new Error(`Error HTTP: ${response.status}`);
                }
                return response.json();
            })
            .then(data => {
                // Verificar si hay un error explícito en la respuesta
                if (data.error) {
                    console.error('Error del servidor:', data.error);
                    setUserTickets([]);
                    setLoading(false);
                    return;
                }

                // Los números ya vienen filtrados por cédula desde el servidor
                // Verificar si existen números en la respuesta, independientemente de success
                const todosLosTickets = Array.isArray(data.numeros) ? data.numeros : [];
                
                // Si hay tickets, procesarlos siempre
                if (todosLosTickets.length > 0) {
                    try {
                        // Agrupar todos los tickets por rifa (para obtener todas las rifas del usuario)
                        const ticketsAgrupados = todosLosTickets.reduce((acc, ticket) => {
                            // Validar que el ticket tenga los campos necesarios
                            if (!ticket || !ticket.idRifa) {
                                return acc;
                            }
                            
                            const rifaId = ticket.idRifa;
                            
                            if (!acc[rifaId]) {
                                acc[rifaId] = {
                                    idRifa: rifaId,
                                    numeros: [],
                                    numerosReservados: [],
                                    numerosPendientes: [],
                                    numerosDisponibles: [],
                                    tieneReservados: false,
                                    tienePendientes: false,
                                    tieneDisponibles: false,
                                    fechaCreacionRifa: ticket.fechaCreacionRifa || ticket.createdAt,
                                    nombre: ticket.nombre,
                                    telefono: ticket.telefono,
                                    email: ticket.email,
                                    direccion: ticket.direccion
                                };
                            }
                            
                            // Agregar el número a la lista de números de la rifa
                            if (ticket.numero) {
                                acc[rifaId].numeros.push(ticket.numero);
                            }
                            
                            // Separar números por estado
                            if (ticket.estado === 'reservado') {
                                if (ticket.numero) {
                                    acc[rifaId].numerosReservados.push(ticket.numero);
                                }
                                acc[rifaId].tieneReservados = true;
                            } else if (ticket.estado === 'pendiente') {
                                if (ticket.numero) {
                                    acc[rifaId].numerosPendientes.push(ticket.numero);
                                }
                                acc[rifaId].tienePendientes = true;
                            } else if (ticket.estado === 'disponible') {
                                if (ticket.numero) {
                                    acc[rifaId].numerosDisponibles.push(ticket.numero);
                                }
                                acc[rifaId].tieneDisponibles = true;
                            }
                            
                            return acc;
                        }, {});
                        
                        // Convertir objeto a array y ordenar por fecha de creación de la rifa
                        const ticketsAgrupadosArray = Object.values(ticketsAgrupados)
                            .sort((a, b) => {
                                const dateA = a.fechaCreacionRifa ? new Date(a.fechaCreacionRifa) : new Date(0);
                                const dateB = b.fechaCreacionRifa ? new Date(b.fechaCreacionRifa) : new Date(0);
                                return dateB - dateA;
                            })
                            .slice(0, 10); // Limitar a 10 rifas
                        
                        // Asegurar que siempre se establezcan los tickets si hay datos procesados
                        if (ticketsAgrupadosArray.length > 0) {
                            setUserTickets(ticketsAgrupadosArray);
                        } else {
                            // Si después del procesamiento no hay tickets, establecer array vacío
                            setUserTickets([]);
                        }
                    } catch (error) {
                        console.error('Error al procesar tickets:', error);
                        // En caso de error, intentar mostrar los tickets sin procesar
                        setUserTickets([]);
                    }
                } else {
                    // No hay tickets en la respuesta
                    setUserTickets([]);
                }
                
                setLoading(false);
            })
            .catch(error => {
                console.error('Error al buscar tickets:', error);
                setUserTickets([]);
                setLoading(false);
            });
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter') {
            handleBuscar();
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'Fecha no disponible';
        const date = new Date(dateString);
        return date.toLocaleDateString('es-VE');
    };

    const getRifaInfo = (idRifa) => {
        return rifas.find(rifa => rifa.idRifa === parseInt(idRifa));
    };

    const getGanador = (idRifa) => {
        const ganador = ganadores.find(g => g.idRifa === parseInt(idRifa));
        return ganador ? ganador.numeroGanador : 'Por definir';
    };

    const handleBackToHome = () => {
        navigate('/');
    };

    return (
        <div className="ticket-verification-page">
            {/* Header */}
            <header className="main-header">
                <div className="header-left">
                    <img 
                        src={logo} 
                        alt="Logo" 
                        className="header-logo" 
                        onClick={() => navigate('/')}
                        style={{ cursor: 'pointer' }}
                    />
                </div>
                <div className="header-center">
                    {/* Espacio para centrar el header */}
                </div>
                <div className="header-right">
                    <button className="menu-button" onClick={() => setIsMenuOpen(true)}>
                        <FontAwesomeIcon icon={faBars} />
                    </button>
                </div>
            </header>

            {/* Main Content */}
            <main className="main-content">
                {/* Title */}
                <div className="section-title">
                    <h1>Verificador de Boletos</h1>
                </div>

                {/* Search Section */}
                <div className="search-section">
                    {/* Fila 1: Input y botón de búsqueda */}
                    <div className="search-container">
                        <div className="search-input-container">
                            <img src={process.env.PUBLIC_URL + '/images/identification.png'} alt="ID" className="search-icon" />
                            <input
                                type="text"
                                className="search-input"
                                placeholder="12345678"
                                value={cedula}
                                onChange={(e) => setCedula(e.target.value)}
                                onKeyPress={handleKeyPress}
                            />
                        </div>
                        <button className="search-button" onClick={handleBuscar}>
                            <FontAwesomeIcon icon={faSearch} />
                        </button>
                    </div>
                    
                    {/* Fila 2: Texto descriptivo - solo cuando no hay datos */}
                    {userTickets.length === 0 && (
                        <p className="search-description">Introduce tu número de cédula</p>
                    )}
                    
                    {/* Fila 3: Imagen cuando no hay datos */}
                    {!hasSearched && (
                        <div className="no-data-image-container">
                            <div className="golden-circle-background"></div>
                            <img src={elfUpImage} alt="No hay datos" className="no-data-image" />
                        </div>
                    )}
                </div>

                {/* Results Section */}
                <div className="tickets-section">
                    {loading ? (
                        <div className="loading-container">
                            <div className="loading-spinner"></div>
                            <p>Buscando tickets...</p>
                        </div>
                    ) : userTickets.length > 0 ? (
                        <div className="tickets-list">
                            {userTickets.map((ticket, index) => {
                                const rifaInfo = getRifaInfo(ticket.idRifa);
                                const ganador = getGanador(ticket.idRifa);
                                
                                return (
                                    <div key={index} className="ticket-item">
                                        <div className="ticket-rifa-id">
                                            Rifa #{ticket.idRifa}
                                        </div>
                                        <div className="ticket-details">
                                            <div className="ticket-detail">
                                                <span className="detail-label">Fecha:</span>
                                                <span className="detail-value">{formatDate(ticket.fechaCreacionRifa)}</span>
                                            </div>
                                            <div className="ticket-detail">
                                                <span className="detail-label">Números:</span>
                                                <span className="detail-value">
                                                    {ticket.tieneReservados 
                                                        ? ticket.numerosReservados.join(', ') 
                                                        : ticket.tienePendientes 
                                                            ? 'Por definir' 
                                                            : ticket.tieneDisponibles && ticket.numerosDisponibles.length > 0
                                                                ? ticket.numerosDisponibles.join(', ')
                                                                : ticket.numeros.length > 0
                                                                    ? ticket.numeros.join(', ')
                                                                    : 'Por Confirmar'}
                                                </span>
                                            </div>
                                            <div className="ticket-detail">
                                                <span className="detail-label">Ganador:</span>
                                                <span className="detail-value">{ganador}</span>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ) : cedula && !loading && hasSearched ? (
                        <div className="no-tickets">
                            {/* Fila 1: Imagen */}
                            <div className="no-data-image-container">
                                <div className="golden-circle-background"></div>
                                <img src={elfUpImage} alt="No hay datos" className="no-data-image" />
                            </div>
                            {/* Fila 2: Texto "No hay datos" */}
                            <p className="no-data-text">No hay datos</p>
                        </div>
                    ) : null}
                </div>
            </main>

            <Footer />

            {/* Menú lateral */}
            <SideMenu isOpen={isMenuOpen} onClose={() => setIsMenuOpen(false)} />
        </div>
    );
}
